///
/// EarthBrowser
///
/// Created by Matt Giger on 9/15/2026
/// Copyright (c) 2026 Geodata Labs
///

import { RadarTextureSeries } from "@/earthbrowser/dataset-distribution/mrms/MrmsOverlay"
import { TextureOverlay } from "@/earthbrowser/datasets/TextureOverlay"
import { type TextureFrame, type TextureSeries } from "@/earthbrowser/datasets/TextureSeries"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { type Material } from "@stovokor/shader/Material"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"

/**
 * Global sea surface temperature overlay from NOAA Coral Reef Watch
 * CoralTemp (daily 5 km analysis). Frames are global equirectangular R8
 * grayscale where 0 = land/nodata and 1..255 maps linearly to -2..35 deg C;
 * the shader colormaps the field with a thermal palette. Consecutive daily
 * frames cross-dissolve as the clock scrubs (the field evolves slowly, so a
 * plain blend looks right - no optical-flow advection needed).
 */
export class SstOverlay extends TextureOverlay {

	constructor(context: EarthBrowserContext) {
		super(context)
		this.textureSeries = new RadarTextureSeries(context)
		// One RG texture for the frame pair: see TextureSeries.packPairs
		this.textureSeries.packPairs = true
	}

	public overlayMaterial(): Material {
		return new SstOverlayMaterial(this.textureSeries)
	}
}

class SstOverlayMaterial implements Material {
	public shaderDefines: Record<string, string> = {
		"SST_TEXTURES": "1"
	}
	public available: boolean = true
	public transparency: boolean = false
	public sstFade = new UniformDynamic<number>()
	public sstBlend = new UniformDynamic<number>()
	public sstPair = new UniformDynamic<Texture>()

	private textureSeries: TextureSeries<TextureFrame>

	constructor(textureSeries: TextureSeries<TextureFrame>) {
		this.textureSeries = textureSeries
	}

	public renderUpdate(_state: RenderState): void {
		this.sstFade.value = this.textureSeries.fadeFactor
		this.sstBlend.value = this.textureSeries.blendFactor
		this.sstPair.value = this.textureSeries.pairTexture
	}
}
